import { useCallback, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";
import { useLatest } from "../../../hooks/useLatest";

type UseDmsOptions<V> = {
  socket: any;
  me?: { name?: string | null; userId?: string | null } | null;
  user?: { username?: string | null; userId?: string | null } | null;
  voids: V[];
  // Navigation/UI callbacks startDmWithUser drives, named after the
  // ChatLayout originals so the body moved verbatim. setDmFriendSearch is a
  // deferred wrapper at the call site (useFriends is called after this hook).
  switchVoid: (id: string) => void;
  switchChannel: (id: string) => void;
  setChannelPaneView: (v: 'spaces' | 'dms') => void;
  setChanSheetOpen: (open: boolean) => void;
  setDmFriendSearch: (v: string) => void;
};

/**
 * DM-surface state: the mobile DM page/search, the start-DM action, the
 * active DM spaces, and the self-name-stripping DM display-name helper
 * (step 4.2).
 *
 * Generic over the space shape so activeDmVoids preserves whatever fields
 * ChatLayout's `voids` carry (DmSidebar reads more than id/name).
 *
 * ChatLayout's call site sits right after channelPaneView's declaration —
 * before the render-time landingFavoriteCards loop, which calls
 * getDmDisplayName during render. switchVoid/switchChannel are hoisted
 * function declarations, so passing them above their definitions is safe.
 */
export function useDms<V extends { id: string; name?: string | null }>({
  socket,
  me,
  user,
  voids,
  switchVoid,
  switchChannel,
  setChannelPaneView,
  setChanSheetOpen,
  setDmFriendSearch,
}: UseDmsOptions<V>) {
  const [dmPageOpen, setDmPageOpen] = useState(false);
  const [dmSearch, setDmSearch] = useState('');
  const [dmSearchOpen, setDmSearchOpen] = useState(false);
  const meName = me?.name;
  const userUsername = user?.username;
  const meUserId = (me as any)?.userId;
  const userUserId = (user as any)?.userId;
  const dmSelfNameTokens = useMemo(() => {
    const normalize = (value: string) => value
      .trim()
      .toLowerCase()
      .replace(/\(you\)/g, '')
      .replace(/^@+/, '')
      .replace(/^[_*~`]+|[_*~`]+$/g, '')
      .replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    const set = new Set<string>();
    const push = (value?: string | null) => {
      const v = normalize(String(value || ''));
      if (v) set.add(v);
    };
    push(meName);
    push(userUsername);
    push(meUserId);
    push(userUserId);
    return set;
  }, [meName, userUsername, meUserId, userUserId]);
  const getDmDisplayName = useCallback((space?: { id?: string; name?: string | null } | null, fallbackId = '') => {
    const id = String(space?.id || fallbackId || '');
    const rawName = String(space?.name || '').trim();
    if (!id.startsWith('dm_')) return rawName || id || 'Untitled space';
    if (!rawName) return 'Direct messages';
    const normalize = (value: string) => value
      .trim()
      .toLowerCase()
      .replace(/\(you\)/g, '')
      .replace(/^@+/, '')
      .replace(/^[_*~`]+|[_*~`]+$/g, '')
      .replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    const cleanDisplay = (value: string) => value
      .trim()
      .replace(/^[_*~`]+|[_*~`]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const isSelfPart = (part: string) => {
      const n = normalize(part);
      if (!n) return false;
      if (dmSelfNameTokens.has(n)) return true;
      if (/\byou\b/.test(part.toLowerCase())) return true;
      const stripped = n.replace(/\([^)]*\)/g, '').trim();
      return !!stripped && dmSelfNameTokens.has(stripped);
    };
    const parts = rawName
      .split(/,|&|\band\b|•|\||\+/gi)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length > 0) {
      const others = parts.filter((p) => !isSelfPart(p));
      if (others.length > 0) return others.map(cleanDisplay).filter(Boolean).join(', ');
      return 'Direct messages';
    }
    if (isSelfPart(rawName)) return 'Direct messages';
    return rawName;
  }, [dmSelfNameTokens]);
  const [dmStartBusyUserId, setDmStartBusyUserId] = useState<string | null>(null);
  // The parent re-creates these every render; read the newest ones when the action runs.
  const navRef = useLatest({ switchVoid, switchChannel, setChannelPaneView, setChanSheetOpen, setDmFriendSearch });
  const startDmWithUser = useCallback(async (uid: string) => {
    if (!uid || dmStartBusyUserId) return;
    setDmStartBusyUserId(uid);
    try {
      const tok = getAuthToken() || '';
      const res = await api.postAuth('/dms/start', { userId: uid }, tok);
      const sid = String(res.spaceId);
      const cid = String(res.channelId);
      socket.emit('void:list');
      socket.emit('channel:list', { voidId: sid });
      const nav = navRef.current;
      nav.switchVoid(sid);
      const shortId = cid.includes(':') ? cid.split(':')[1] : cid;
      nav.switchChannel(shortId);
      nav.setChannelPaneView('dms');
      nav.setChanSheetOpen(false);
      nav.setDmFriendSearch('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to start DM';
      toast(msg || 'Failed to start DM', 'error');
    } finally {
      setDmStartBusyUserId((prev) => (prev === uid ? null : prev));
    }
  }, [dmStartBusyUserId, socket, navRef]);
  const activeDmVoids = useMemo(() => {
    return voids
      .filter((v) => String(v.id).startsWith('dm_'))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [voids]);

  return {
    dmPageOpen,
    setDmPageOpen,
    dmSearch,
    setDmSearch,
    dmSearchOpen,
    setDmSearchOpen,
    dmStartBusyUserId,
    startDmWithUser,
    activeDmVoids,
    getDmDisplayName,
  };
}


